import format from 'date-fns/format';
import { ru } from 'date-fns/locale';

import BackIcon from '../assets/icons/back.svg?react';
import ChevronUpIcon from '../assets/icons/chevron-up.svg?react';
import EditPatientIcon from '../assets/icons/edit-patient.svg?react';
import EditProfileIcon from '../assets/icons/edit-profile.svg?react';
import LogoutIcon from '../assets/icons/logout.svg?react';
import MenuIcon from '../assets/icons/menu.svg?react';
import PlusIcon from '../assets/icons/plus.svg?react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PeopleApi } from '../api';
import {
  CreateAddressModal,
  CreatePatientModal,
  DefaultAvatarIcon,
  EditPatientModal,
  EditProfileModal,
} from '../components';
import { useAuthContext, useLegalMutationsAllowed, useProfile } from '../hooks';
import type { AddressResponse } from '../model/Address';

type OtherPatient = {
  id: number;
  fullName: string;
  birthdate: string;
  phone: string;
  email: string;
  address: string;
};

const mockOtherPatients: OtherPatient[] = [];

export const ClientProfilePage = () => {
  const [activeTab, setActiveTab] = useState<'addresses' | 'payments'>(
    'payments',
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressResponse | null>(
    null,
  );
  const [isCreatePatientModalOpen, setIsCreatePatientModalOpen] =
    useState(false);
  const { allowed: legalMutationsAllowed, requireConsent } =
    useLegalMutationsAllowed();

  const guardMutation = (action: () => void) => {
    if (!legalMutationsAllowed) {
      requireConsent();
      return;
    }
    action();
  };
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [editingPatientId, setEditingPatientId] = useState<number | null>(null);
  const [isPaymentsCollapsed, setIsPaymentsCollapsed] = useState(false);
  const [isAddressesCollapsed, setIsAddressesCollapsed] = useState(false);
  const [isOtherPatientsCollapsed, setIsOtherPatientsCollapsed] =
    useState(false);
  const [addressMenuOpen, setAddressMenuOpen] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const addressMenuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const addressButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>(
    {},
  );
  const profile = useProfile(true);
  const navigate = useNavigate();
  const { signOut } = useAuthContext();
  const queryClient = useQueryClient();

  const { data: addressesData } = useQuery({
    queryKey: [PeopleApi.KEY, 'addresses', profile?.people_main_id],
    queryFn: () => PeopleApi.getAddress(profile?.people_main_id),
    enabled: !!profile?.people_main_id,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressMenuOpen !== null) {
        const menuElement = addressMenuRefs.current[addressMenuOpen];
        const buttonElement = addressButtonRefs.current[addressMenuOpen];
        if (
          menuElement &&
          buttonElement &&
          !menuElement.contains(event.target as Node) &&
          !buttonElement.contains(event.target as Node)
        ) {
          setAddressMenuOpen(null);
          setMenuPosition(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [addressMenuOpen]);

  useEffect(() => {
    const updateMenuPosition = () => {
      if (addressMenuOpen !== null) {
        const buttonElement = addressButtonRefs.current[addressMenuOpen];
        if (buttonElement) {
          const rect = buttonElement.getBoundingClientRect();
          const menuWidth = 160; // ширина меню
          const spacing = 4; // отступ от кнопки

          // Вычисляем позицию справа от кнопки
          let left = rect.right + window.scrollX - menuWidth;

          // Проверяем, не выходит ли меню за левый край экрана
          if (left < window.scrollX) {
            left = rect.left + window.scrollX;
          }

          // Проверяем, не выходит ли меню за правый край экрана
          if (left + menuWidth > window.scrollX + window.innerWidth) {
            left = window.scrollX + window.innerWidth - menuWidth - 8; // 8px отступ от края
          }

          setMenuPosition({
            top: rect.bottom + window.scrollY + spacing,
            left: left,
          });
        }
      }
    };

    if (addressMenuOpen !== null) {
      updateMenuPosition();
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
    }

    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [addressMenuOpen]);

  const avatarUrl = profile?.avatar;
  const fullName = profile?.fullName || profile?.name || 'Пользователь';
  const email = profile?.email || '';
  const birthdate = profile?.birthdate
    ? format(new Date(profile.birthdate), 'd MMMM yyyy г', {
        locale: ru,
      })
    : '';

  const cards = profile?.cards || [];
  const addresses = addressesData?.items || profile?.address || [];

  const displayedAddresses = isAddressesCollapsed
    ? addresses.slice(0, 3)
    : addresses;

  const displayedCards = isPaymentsCollapsed ? cards.slice(0, 3) : cards;

  const displayedPatients = isOtherPatientsCollapsed
    ? mockOtherPatients.slice(0, 3)
    : mockOtherPatients;

  const handleAddAddress = () => {
    setEditingAddress(null);
    setIsAddressModalOpen(true);
  };

  const handleEditAddress = (address: AddressResponse) => {
    setEditingAddress(address);
    setIsEditAddressModalOpen(true);
    setAddressMenuOpen(null);
  };

  const handleDeleteAddress = async (addressId: number) => {
    // TODO: Implement delete address API call
    toast.success('Адрес удален');
    queryClient.invalidateQueries([
      PeopleApi.KEY,
      'addresses',
      profile?.people_main_id,
    ]);
    setAddressMenuOpen(null);
  };

  const handleAddressSelect = (address: AddressResponse) => {
    queryClient.invalidateQueries([
      PeopleApi.KEY,
      'addresses',
      profile?.people_main_id,
    ]);
    setIsAddressModalOpen(false);
    setIsEditAddressModalOpen(false);
    setEditingAddress(null);
  };

  return (
    <>
      <div className="relative flex w-full items-center justify-between gap-3 pb-[24px] pl-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="relative flex flex-[0_0_auto] items-center gap-3 opacity-50"
        >
          <BackIcon className="relative size-5" />
          <div className="relative mt-[-1.00px] w-fit whitespace-nowrap text-[1rem] font-normal leading-6 tracking-normal text-[#151515]">
            Вернуться назад
          </div>
        </button>
        <button
          type="button"
          onClick={() => signOut()}
          className="relative flex flex-[0_0_auto] items-center gap-3 opacity-50"
        >
          <div className="relative mt-[-1.00px] w-fit whitespace-nowrap text-[1rem] font-normal leading-6 tracking-normal text-[#151515]">
            Выйти
          </div>
          <LogoutIcon className="relative size-5" />
        </button>
      </div>

      <div className="relative inline-flex w-full flex-col items-start overflow-hidden rounded-3xl bg-gradient-to-b from-[#151515] via-[#151515] to-white">
        <div className="relative flex w-full items-center justify-around gap-[509px] rounded-[16px_16px_0px_0px] px-6 py-4">
          <div className="relative flex flex-1 flex-col items-center gap-6 md:flex-row">
            {avatarUrl ? (
              <div
                className="relative size-[80px] rounded-[100px] bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${avatarUrl})` }}
              />
            ) : (
              <DefaultAvatarIcon className="size-[80px]" />
            )}
            <div className="relative flex flex-1 flex-col items-start justify-center gap-1">
              <div className="-mt-px w-fit whitespace-nowrap text-2xl font-bold leading-9 tracking-normal text-white [font-family:'Ubuntu',Helvetica]">
                {fullName}
              </div>
              <div className="inline-flex items-start gap-1.5">
                {email && (
                  <>
                    <div className="-mt-px w-fit whitespace-nowrap text-base leading-6 text-white opacity-40 [font-family:'Ubuntu',Helvetica]">
                      {email}
                    </div>
                    {birthdate && (
                      <>
                        <div className="-mt-px w-fit whitespace-nowrap text-base leading-6 text-white opacity-20">
                          •
                        </div>
                        <div className="-mt-px w-fit whitespace-nowrap text-base leading-6 text-white opacity-40 [font-family:'Ubuntu',Helvetica]">
                          {birthdate}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => guardMutation(() => setIsEditModalOpen(true))}
              aria-disabled={!legalMutationsAllowed}
              className={`absolute right-0 flex size-[44px] items-center justify-center rounded-[50%] bg-white/10 transition-colors md:relative md:right-auto ${
                legalMutationsAllowed
                  ? 'hover:bg-white/20'
                  : 'cursor-not-allowed opacity-50'
              }`}
            >
              <EditProfileIcon className="size-[44px]" />
            </button>
          </div>
        </div>

        <div className="relative flex w-full flex-col items-start gap-8 rounded-3xl bg-white px-0 py-6">
          <div className="flex w-full flex-col items-center justify-center gap-6 px-6">
            <div className="flex w-full items-center overflow-hidden rounded-[100px] bg-[#f7f8f9]">
              <button
                className={`flex flex-1 items-center justify-center gap-2  rounded-[100px] p-4 ${
                  activeTab === 'addresses'
                    ? 'bg-[#151515] text-[#f6f8f9]'
                    : 'bg-[#f6f8f9] text-[#151515]/50'
                }`}
                onClick={() => setActiveTab('addresses')}
                type="button"
              >
                Мои адреса
              </button>
              <button
                className={`flex flex-1 items-center justify-center gap-2 rounded-[100px] p-4 ${
                  activeTab === 'payments'
                    ? 'bg-[#151515] text-[#f6f8f9]'
                    : 'bg-[#f6f8f9] text-[#151515]/50'
                }`}
                onClick={() => setActiveTab('payments')}
                type="button"
              >
                Способы оплаты
              </button>
            </div>

            {activeTab === 'payments' && (
              <div className="flex w-full flex-col items-center justify-center gap-4">
                <div className="flex w-full flex-col items-start gap-5">
                  <div className="flex w-full items-start">
                    <div className="flex flex-1 items-baseline gap-2">
                      <div className="-mt-px w-fit whitespace-nowrap text-xl font-medium leading-[30px] text-[#151515]">
                        Способы оплаты
                      </div>
                      <div className="w-fit whitespace-nowrap text-sm leading-5 text-black opacity-40">
                        (у вас {cards.length})
                      </div>
                    </div>
                  </div>

                  <div className="flex w-full flex-col items-start gap-4">
                    {displayedCards.length > 0 ? (
                      <>
                        <svg
                          className="-mt-px h-px w-full"
                          viewBox="0 0 912 1"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          preserveAspectRatio="none"
                        >
                          <line
                            opacity="0.1"
                            x1="-4.37081e-08"
                            y1="0.500038"
                            x2="911.93"
                            y2="0.499958"
                            stroke="#151515"
                            vectorEffect="non-scaling-stroke"
                          />
                        </svg>
                        {displayedCards.map((card, index) => {
                          const lastFour =
                            card.card_number_masked?.slice(-4) || '';
                          const isPrimary = index === 0;
                          return (
                            <div
                              key={card.id}
                              className="flex w-full items-center gap-3 px-0 py-2"
                            >
                              <div className="flex h-8 w-11 items-center justify-center rounded bg-[#f1f7fa]">
                                <span className="text-xs font-medium text-[#151515]">
                                  {lastFour ? `****${lastFour}` : '****'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <div className="-mt-px w-fit whitespace-nowrap text-base leading-6 text-[#151515]">
                                  {card.card_number_masked || 'Карта'}
                                </div>
                                {isPrimary && (
                                  <>
                                    <div className="w-fit whitespace-nowrap text-sm leading-5 text-black">
                                      •
                                    </div>
                                    <div className="-mt-px w-fit whitespace-nowrap text-base leading-6 text-[#151515] opacity-80">
                                      (основной)
                                    </div>
                                  </>
                                )}
                              </div>
                              <svg
                                className="h-px w-full"
                                viewBox="0 0 912 1"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                preserveAspectRatio="none"
                              >
                                <line
                                  opacity="0.1"
                                  x1="-4.37081e-08"
                                  y1="0.500038"
                                  x2="911.93"
                                  y2="0.499958"
                                  stroke="#151515"
                                  vectorEffect="non-scaling-stroke"
                                />
                              </svg>
                              <div className="relative">
                                <MenuIcon />
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <div className="w-full py-[80px] text-center text-[#151515] opacity-50">
                        Нет привязанных карт
                      </div>
                    )}
                  </div>
                </div>
                {cards.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setIsPaymentsCollapsed(!isPaymentsCollapsed)}
                    className="inline-flex items-center gap-2 rounded-[16px] bg-[#f7f8f9] px-3 py-1.5"
                  >
                    <div className="-mt-px w-fit whitespace-nowrap text-sm leading-5 text-[#151515]">
                      Свернуть
                    </div>
                    <ChevronUpIcon
                      className={`relative size-2.5 transition-transform ${
                        isPaymentsCollapsed ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                )}
              </div>
            )}

            {activeTab === 'addresses' && (
              <div className="flex w-full flex-col items-center justify-center gap-4">
                <div className="flex w-full flex-col items-start gap-5">
                  <div className="flex w-full items-start">
                    <div className="flex flex-1 items-baseline gap-2">
                      <div className="-mt-px w-fit whitespace-nowrap text-xl font-medium leading-[30px] text-[#151515]">
                        Адреса
                      </div>
                      <div className="w-fit whitespace-nowrap text-sm leading-5 text-black opacity-40">
                        (у вас {addresses.length})
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => guardMutation(handleAddAddress)}
                      aria-disabled={!legalMutationsAllowed}
                      className={`inline-flex items-center gap-3 ${
                        legalMutationsAllowed
                          ? 'cursor-pointer'
                          : 'cursor-not-allowed opacity-50'
                      }`}
                    >
                      <div className="inline-flex size-[28px] items-start gap-[24px] rounded-[8px] bg-[#e4f0f7] p-[6px]">
                        <div className="flex size-[16px] items-center justify-center">
                          <PlusIcon />
                        </div>
                      </div>
                      <div className="w-fit whitespace-nowrap text-base leading-6 text-[#151515]">
                        Добавить
                      </div>
                    </button>
                  </div>

                  <div className="flex w-full flex-col items-start gap-4">
                    {displayedAddresses.length > 0 ? (
                      <>
                        <svg
                          className="-mt-px h-px w-full"
                          viewBox="0 0 912 1"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          preserveAspectRatio="none"
                        >
                          <line
                            opacity="0.1"
                            x1="-4.37081e-08"
                            y1="0.500038"
                            x2="911.93"
                            y2="0.499958"
                            stroke="#151515"
                            vectorEffect="non-scaling-stroke"
                          />
                        </svg>
                        {displayedAddresses.map((a, index) => (
                          <div key={a.id} className="w-full overflow-y-auto">
                            <div className="flex w-full items-center justify-center gap-0.5 rounded-[20px] px-0 py-2">
                              <div className="flex flex-1 flex-col items-start justify-center gap-2">
                                <div className="flex w-full items-center gap-3">
                                  <svg
                                    className="size-5"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M8 0C5.2 0 3 2.2 3 5C3 9 8 16 8 16C8 16 13 9 13 5C13 2.2 10.8 0 8 0ZM8 6.5C7.2 6.5 6.5 5.8 6.5 5C6.5 4.2 7.2 3.5 8 3.5C8.8 3.5 9.5 4.2 9.5 5C9.5 5.8 8.8 6.5 8 6.5Z"
                                      fill="#151515"
                                      opacity="0.4"
                                    />
                                  </svg>
                                  <div className="inline-flex items-center gap-2">
                                    <p className="-mt-px w-fit whitespace-nowrap text-base leading-6 text-[#151515]">
                                      {a.address_text ||
                                        a.text ||
                                        'Адрес не указан'}
                                    </p>
                                    {index === 0 && (
                                      <div className="w-fit whitespace-nowrap text-sm leading-5 text-black opacity-40">
                                        (основной)
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="relative">
                                <button
                                  type="button"
                                  ref={(el) => {
                                    addressButtonRefs.current[a.id] = el;
                                  }}
                                  onClick={() =>
                                    guardMutation(() =>
                                      setAddressMenuOpen(
                                        addressMenuOpen === a.id ? null : a.id,
                                      ),
                                    )
                                  }
                                  aria-disabled={!legalMutationsAllowed}
                                  className={`relative ${
                                    legalMutationsAllowed
                                      ? ''
                                      : 'cursor-not-allowed opacity-50'
                                  }`}
                                >
                                  <MenuIcon />
                                </button>
                              </div>
                            </div>
                            {index < displayedAddresses.length - 1 && (
                              <svg
                                className="h-px w-full"
                                viewBox="0 0 912 1"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                preserveAspectRatio="none"
                              >
                                <line
                                  opacity="0.1"
                                  x1="-4.37081e-08"
                                  y1="0.500038"
                                  x2="911.93"
                                  y2="0.499958"
                                  stroke="#151515"
                                  vectorEffect="non-scaling-stroke"
                                />
                              </svg>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="w-full py-[80px] text-center text-[#151515] opacity-50">
                        Нет сохраненных адресов
                      </div>
                    )}
                  </div>
                </div>
                {addresses.length > 3 && (
                  <button
                    type="button"
                    onClick={() =>
                      setIsAddressesCollapsed(!isAddressesCollapsed)
                    }
                    className="inline-flex items-center gap-2 rounded-[16px] bg-[#f7f8f9] px-3 py-1.5"
                  >
                    <div className="-mt-px w-fit whitespace-nowrap text-sm leading-5 text-[#151515]">
                      Свернуть
                    </div>
                    <ChevronUpIcon
                      className={`relative size-2.5 transition-transform ${
                        isAddressesCollapsed ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Другие пациенты */}
      <div className="mt-6 flex w-full flex-col items-start overflow-hidden rounded-2xl bg-[#e4f0f7]">
        <button
          type="button"
          onClick={() => setIsOtherPatientsCollapsed(!isOtherPatientsCollapsed)}
          className={`flex w-full items-center justify-between bg-[#e4f0f7] px-6 py-4 ${
            isOtherPatientsCollapsed
              ? 'rounded-2xl'
              : 'rounded-[16px_16px_0px_0px]'
          }`}
        >
          <div className="-mt-px w-fit whitespace-nowrap text-xl font-medium leading-[30px] text-[#151515]">
            Другие пациенты
          </div>
          <ChevronUpIcon
            className={`relative size-[14px] transition-transform ${
              isOtherPatientsCollapsed ? 'rotate-180' : ''
            }`}
          />
        </button>
        {!isOtherPatientsCollapsed && (
          <div
            className={`flex w-full flex-col items-start gap-6 bg-white p-6 ${
              isOtherPatientsCollapsed
                ? 'rounded-[0px_0px_16px_16px]'
                : 'rounded-[16px]'
            }`}
          >
            {displayedPatients.map((p, idx) => (
              <div key={p.id} className="w-full">
                <div className="flex w-full items-center gap-4">
                  <div className="w-fit text-lg leading-6 text-[#151515] opacity-30">
                    {idx + 1}
                  </div>
                  <div className="flex-1 text-lg font-bold leading-6 text-[#151515]">
                    {p.fullName}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      guardMutation(() => {
                        setEditingPatientId(p.id);
                        setIsEditPatientModalOpen(true);
                      })
                    }
                    aria-disabled={!legalMutationsAllowed}
                    className={`relative flex items-center justify-center ${
                      legalMutationsAllowed
                        ? ''
                        : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <EditPatientIcon className="relative size-[20px]" />
                  </button>
                </div>
                <div className="mt-5 flex w-full flex-col items-start gap-5">
                  <div className="flex w-full items-center gap-5">
                    <div className="flex flex-1 flex-col items-start gap-1">
                      <div className="text-sm leading-5 text-[#15151580]">
                        Дата рождения
                      </div>
                      <div className="text-base leading-6 text-[#151515]">
                        {p.birthdate}
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col items-start gap-1">
                      <div className="text-sm leading-5 text-[#15151580]">
                        Телефон
                      </div>
                      <div className="text-base leading-6 text-[#151515]">
                        {p.phone}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full items-start gap-5">
                    <div className="flex flex-1 flex-col items-start gap-1">
                      <div className="text-sm leading-5 text-[#15151580]">
                        Email
                      </div>
                      <div className="text-base leading-6 text-[#151515]">
                        {p.email}
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col items-start gap-1">
                      <div className="text-sm leading-5 text-[#15151580]">
                        Адрес проживания
                      </div>
                      <div className="text-base leading-6 text-[#151515]">
                        {p.address}
                      </div>
                    </div>
                  </div>
                </div>
                {idx < displayedPatients.length - 1 && (
                  <div className="mt-6">
                    <svg
                      className="h-px w-full"
                      viewBox="0 0 912 1"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      preserveAspectRatio="none"
                    >
                      <line
                        opacity="0.1"
                        x1="-4.37081e-08"
                        y1="0.500038"
                        x2="911.93"
                        y2="0.499958"
                        stroke="#151515"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                guardMutation(() => setIsCreatePatientModalOpen(true))
              }
              aria-disabled={!legalMutationsAllowed}
              className={`inline-flex items-center gap-3 ${
                legalMutationsAllowed
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed opacity-50'
              }`}
            >
              <div className="inline-flex size-[28px] items-start gap-[24px] rounded-[8px] bg-[#e4f0f7] p-[6px]">
                <div className="flex size-[16px] items-center justify-center">
                  <PlusIcon />
                </div>
              </div>
              <div className="w-fit whitespace-nowrap text-base leading-6 text-[#2c9aee]">
                Добавить
              </div>
            </button>
          </div>
        )}
      </div>
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
      <CreateAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => {
          setIsAddressModalOpen(false);
          setEditingAddress(null);
        }}
        patientId={profile?.people_main_id || null}
        initialData={null}
        onAddressSelect={handleAddressSelect}
      />
      <CreateAddressModal
        isOpen={isEditAddressModalOpen}
        onClose={() => {
          setIsEditAddressModalOpen(false);
          setEditingAddress(null);
        }}
        patientId={profile?.people_main_id || null}
        initialData={editingAddress}
        onAddressSelect={handleAddressSelect}
      />
      <CreatePatientModal
        isOpen={isCreatePatientModalOpen}
        onClose={() => setIsCreatePatientModalOpen(false)}
      />
      <EditPatientModal
        isOpen={isEditPatientModalOpen}
        onClose={() => {
          setIsEditPatientModalOpen(false);
          setEditingPatientId(null);
        }}
        patientId={editingPatientId}
      />
      {addressMenuOpen !== null &&
        menuPosition &&
        (() => {
          const address = displayedAddresses.find(
            (a) => a.id === addressMenuOpen,
          );
          if (!address) return null;

          return createPortal(
            <div
              ref={(el) => {
                if (el && addressMenuOpen !== null) {
                  addressMenuRefs.current[addressMenuOpen] = el;
                }
              }}
              className="fixed z-[9999] min-w-[160px] rounded-[12px] border border-[#151515]/10 bg-white shadow-[0px_8px_24px_rgba(21,21,21,0.12)]"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  handleEditAddress(address);
                  setAddressMenuOpen(null);
                  setMenuPosition(null);
                }}
                className="w-full cursor-pointer px-[12px] py-[10px] text-left text-[14px] leading-5 text-[#151515] first:rounded-t-md hover:bg-[#f1f7fa]"
              >
                Изменить
              </button>
              <button
                type="button"
                onClick={() => {
                  handleDeleteAddress(address.id);
                  setAddressMenuOpen(null);
                  setMenuPosition(null);
                }}
                className="w-full cursor-pointer px-[12px] py-[10px] text-left text-[14px] leading-5 text-[#f93e3e] last:rounded-b-md hover:bg-[#f1f7fa]"
              >
                Удалить
              </button>
            </div>,
            document.body,
          );
        })()}
    </>
  );
};
